import {
  DataGrid,
  DataGridActions,
  DataGridColumns,
  DataGridSections,
  DataGridSelectedActions,
} from '@components/DataGrid/DataGrid'
import { DataGridPerson } from '@components/DataGrid/DataGridPerson'
import ExampleUser from '@assets/exampleUser.png'
import { useNavigate } from 'react-router-dom'
import { RoutePaths } from '@interfaces/routePaths'
import { Alert, Anchor, Drawer, Group, Stack, Text, Title } from '@mantine/core'
import { randomArrayItem } from '@utils/randomArrayItem'
import { generateRandom } from '@utils/randomNumber'
import { Dot } from '@components/Dot/Dot'
import { useState } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'

interface Referral {
  contact: string
  source: {
    name: string
    title: string
    location: string
    phone: string
  }
  context?: string
}

export const Pending = () => {
  const navigate = useNavigate()
  const [drawerOpened, setDrawerOpened] = useState<boolean>(false)
  const [activeReferral, setActiveReferral] = useState<Referral | null>(null)
  const [bannerOpened, setBannerOpened] = useState<boolean>(true)

  const handleNavigate = () =>
    navigate(`../${RoutePaths.RECORD}/123/${RoutePaths.RECORD_HOME}`, {
      state: {
        history: [
          {
            title: 'Community',
            href: `${RoutePaths.COMMUNITY}/${RoutePaths.COMMUNITY_INVITES}`,
          },
          {
            title: 'Pending',
            href: `${RoutePaths.COMMUNITY}/${RoutePaths.COMMUNITY_PENDING}`,
          },
        ],
      },
    })

  const handleDrawerClose = () => setDrawerOpened(false)
  const handleReferralClick = (ref: Referral) => {
    setDrawerOpened(true)
    setActiveReferral(ref)
  }

  const data = Array(30)
    .fill(0)
    .map((_, i) => {
      const hhr = generateRandom(0, 7)
      const cr = generateRandom(0, 7)
      const type = randomArrayItem(['Invited', 'Referral', 'Unknown'])

      return {
        id: `pending-${i}`,
        name: (
          <DataGridPerson
            name="Alfredo Carrillo"
            image={ExampleUser}
            status="online"
            onClick={handleNavigate}
          />
        ),
        inviteType:
          type === 'Referral' ? (
            <Group wrap="nowrap" gap={4}>
              <Anchor
                size="sm"
                onClick={() =>
                  handleReferralClick({
                    contact: 'Alfredo Carrillo',
                    source: {
                      name: 'Cassandra G.',
                      title: 'Case Manager',
                      location: `Children's Aid NYC`,
                      phone: '+1 (555) 321-1212',
                    },
                    context:
                      'This family needs help filling out and submitting paperwork with ICE.',
                  })
                }
              >
                Referral
              </Anchor>
            </Group>
          ) : (
            type
          ),
        invitedBy: 'Joseph Cadle',
        householdRecords: hhr === 0 ? null : hhr,
        childRecords: cr === 0 ? null : cr,
        totalRecords: hhr + cr,
        inviteSent: 'Mar 7, 2024',
        phone: (
          <Anchor href="tel:+1 (212) 555-1212" size="sm" c="dark.7">
            +1 (212) 555-1212
          </Anchor>
        ),
        email: (
          <Anchor href="mailto:alfredo@carrillo.com" size="sm" c="dark.7">
            alfredo@mybluecard.org
          </Anchor>
        ),
        acceptBy: 'Apr 7, 2024',
      }
    })

  const columns: DataGridColumns[] = [
    {
      label: 'Name',
      prop: 'name',
      pinned: true,
    },
    {
      label: 'Invite type',
      prop: 'inviteType',
      pinned: true,
    },
    {
      label: 'Invited By',
      prop: 'invitedBy',
    },
    {
      label: 'Invite sent',
      prop: 'inviteSent',
    },
    {
      label: 'Household Records',
      prop: 'householdRecords',
      centered: true,
    },
    {
      label: 'Child Records',
      prop: 'childRecords',
      centered: true,
    },
    {
      label: 'Total Records',
      prop: 'totalRecords',
      centered: true,
    },
    {
      label: 'Primary Phone',
      prop: 'phone',
    },
    {
      label: 'Primary Email',
      prop: 'email',
    },
    {
      label: 'Accept By',
      prop: 'acceptBy',
      pinned: true,
      defaultSort: true,
    },
  ]

  const sections: DataGridSections[] = [
    {
      label: 'Contacts',
      href: '#',
      default: true,
    },
    {
      label: 'Children',
      href: '#',
    },
  ]

  const actions: DataGridActions[] = [
    {
      label: 'Accept',
      onClick: (item: any) => console.log('Actions: Accept', item),
    },
    {
      label: 'Reject',
      color: 'red',
      onClick: (item: any) => console.log('Actions: Reject', item),
    },
  ]

  const selectedActions: DataGridSelectedActions[] = [
    {
      label: 'Accept',
      onClick: (items: any) => console.log('Selected Actions: Accept'),
    },
    {
      label: 'Reject',
      onClick: (items: any) => console.log('Selected Actions: Reject'),
    },
    {
      label: 'Select parents / guardians',
      section: 'Children',
      onClick: (items: any) =>
        console.log('Selected Actions: Select parents / guardians'),
    },
    {
      label: 'Select children',
      section: 'Contacts',
      onClick: (items: any) => console.log('Selected Actions: Select children'),
    },
  ]

  const handleBannerClosed = () => setBannerOpened(false)

  return (
    <>
      {bannerOpened && (
        <Alert
          variant="light"
          color="red"
          withCloseButton
          title="10 pending records are subject to automatic rejection if not accepted within the next 10 days."
          icon={<IconInfoCircle />}
          m="md"
          onClose={handleBannerClosed}
          mb={0}
        >
          Pending records must be accepted within 30 days of being shared or
          they are subject to automatic rejection.
        </Alert>
      )}

      <DataGrid
        data={data}
        columns={columns}
        sections={sections}
        actions={actions}
        results={data.length}
        selectedActions={selectedActions}
        page={1}
      />

      <Drawer
        opened={drawerOpened}
        onClose={handleDrawerClose}
        title={
          <div>
            <Title order={4}>Referral Details</Title>
          </div>
        }
        position="right"
      >
        <Stack gap="xl" mt="md">
          <Stack gap={3}>
            <Text tt="uppercase" size="xs" c="gray.6" fw="bold" mb="xs">
              Contact
            </Text>
            <Text>{activeReferral?.contact}</Text>
          </Stack>

          <Stack gap={3}>
            <Text tt="uppercase" size="xs" c="gray.6" fw="bold" mb="xs">
              Referral Source
            </Text>
            <Text>{activeReferral?.source.name}</Text>
            <Text>{activeReferral?.source.title}</Text>
            <Text>{activeReferral?.source.location}</Text>
            <Anchor
              href={`tel:${activeReferral?.source.phone}`}
              size="md"
              c="dark.7"
            >
              {activeReferral?.source.phone}
            </Anchor>
          </Stack>

          <Stack gap={3}>
            <Text tt="uppercase" size="xs" c="gray.6" fw="bold" mb="xs">
              Referral Context
            </Text>
            <Text>{activeReferral?.context}</Text>
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}
